/**
 * PendingPlaidResumeBanner — iter1204-plaid-ux.
 *
 * Shows on /banks when the user started a Plaid connect flow that didn't
 * finish — typically because they closed the tab after the bank OAuth
 * roundtrip but before /plaid/exchange-token completed. Without this
 * banner, the bank stays unconnected and the user has to guess whether to
 * click "Connect" again or wait.
 *
 * Sourced from localStorage (`plaid_pending_connect`), populated by
 * `plaidLinkStorage.savePlaidLinkToken(...)` at Link-open time and
 * advanced through phases by PlaidOAuthResumePage. Auto-hides once the
 * marker expires (30 min TTL) or the user clicks "Dismiss".
 *
 * Two actions:
 *   Resume  → reissues a fresh link_token + reopens Plaid Link
 *   Dismiss → clears the marker
 */
import React, { useEffect, useState, useCallback } from 'react';
import { AlertCircle, RefreshCw, X, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  readPendingPlaidConnect,
  clearPendingPlaidConnect,
  savePlaidLinkToken,
} from '../../lib/plaidLinkStorage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Wait a moment after mount before showing so we don't flash the banner
// during the OAuth resume redirect itself (page 1 → resume page → /banks).
const SHOW_DELAY_MS = 1200;

export default function PendingPlaidResumeBanner({ onResumed }) {
  const [pending, setPending] = useState(null);
  const [visible, setVisible] = useState(false);
  const [resuming, setResuming] = useState(false);

  useEffect(() => {
    const p = readPendingPlaidConnect();
    if (!p) return;
    // Only show the banner for "user closed mid-flow" / "OAuth returned
    // but tab closed" phases. Not for `link_opened` (that's the very
    // start — user may still be in Plaid Link right now).
    const showPhases = new Set([
      'oauth_returned',
      'oauth_returned_user_closed_overlay',
      'exchanging', // rare — exchange started but was interrupted
    ]);
    if (!showPhases.has(p.phase)) return;
    setPending(p);
    const t = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const handleResume = useCallback(async () => {
    setResuming(true);
    try {
      const res = await axios.post(`${API}/plaid/create-link-token`);
      const linkToken = res.data?.link_token;
      if (!linkToken) throw new Error('No link_token returned');
      savePlaidLinkToken(linkToken, '/banks', {
        name: pending?.institution_name || null,
      });
      // Let MyBanksPage know to open its Plaid launcher with this token.
      if (typeof onResumed === 'function') {
        onResumed({ linkToken });
      }
      // Hide the banner — it will be re-shown only if the flow stalls
      // again.
      clearPendingPlaidConnect();
      setVisible(false);
      setPending(null);
    } catch (e) {
      toast.error(
        e.response?.data?.detail || 'Could not restart bank connection. Please try again.'
      );
    } finally {
      setResuming(false);
    }
  }, [pending, onResumed]);

  const handleDismiss = () => {
    clearPendingPlaidConnect();
    setVisible(false);
    setPending(null);
  };

  if (!visible || !pending) return null;

  const label = pending.institution_name
    ? `${pending.institution_name} connection`
    : 'A bank connection';

  return (
    <div
      className="border border-amber-300 bg-amber-50 rounded-lg p-3 sm:p-4 flex items-start gap-3"
      data-testid="pending-plaid-resume-banner"
    >
      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
        <AlertCircle className="w-4 h-4 text-amber-700" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-amber-900" data-testid="pending-plaid-resume-title">
          {label} didn&apos;t finish
        </p>
        <p className="text-xs text-amber-800 mt-0.5">
          It looks like you left the connection process before it completed. Pick up where
          you left off, or start fresh if you&apos;d prefer.
        </p>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <button
          type="button"
          onClick={handleResume}
          disabled={resuming}
          className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium px-3 py-1.5 transition-colors disabled:opacity-70"
          data-testid="pending-plaid-resume-btn"
        >
          {resuming ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5" />
          )}
          Resume
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="inline-flex items-center justify-center rounded-md text-amber-800 hover:bg-amber-100 h-7 w-7 transition-colors"
          data-testid="pending-plaid-resume-dismiss-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
